import React, { useCallback, useEffect, useMemo, useState } from "react";
import { METRIC_EVENT_COUNT } from "constants/metrics";
import { DIMENSION_DATE, DIMENSION_EVENT_NAME } from "constants/dimensions";
import {
  DIMENSION_ITEM_CATEGORY,
  DIMENSION_ITEM_CATEGORY2,
  DIMENSION_ITEM_ID,
  DIMENSION_ITEM_ID_TEMP,
  DIMENSION_ITEM_NAME,
  DIMENSION_ITEM_SOURCE,
  DIMENSION_ITEM_URL,
} from "constants/customDimensions";
import { get, isEmpty, orderBy, sum } from "lodash";
import { Card, Col, Row, Spin } from "antd";
import { GRID_GUTTER } from "constants/ui";
import LineChart from "components/charts/LineChart";
import {
  getBatchReports,
  getContentTypeName,
  reviseReport,
  reviseTimeSeriesData,
} from "services/gaService";
import {
  FILTER_EVENT_VIEW_ITEM_DETAILS,
  buildInListFilter,
} from "services/gaDimensionsService";
import { EVENT_VIEW_ITEM_DETAILS } from "constants/customEvents";
import ContentType from "components/ContentType";
import Scorecard from "components/charts/Scorecard";
import TableChart from "components/charts/TableChart";
import ChartTitle from "components/ChartTitle";
import { useReportContext } from "components/ReportContext/ReportContextProvider";
import { v4 as uuid } from "uuid";
import { SOURCE_PLUGILO } from "constants/contentSources";
import { alertUnknownError } from "services/notificationService";
import { CATEGORY_PLUGILO_VIDEO } from "constants/contentCategories";
import { useFormatNumber } from "hooks/useFormatNumber";

const syncNamesOptions = {
  idKey: DIMENSION_ITEM_ID,
  nameKey: DIMENSION_ITEM_NAME,
  typeKey: DIMENSION_ITEM_CATEGORY,
};

const Marketing = () => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { dateStrings } = useReportContext();
  const formatNumber = useFormatNumber();
  const buildFilters = (filters) => {
    const defaultFilters = [
      buildInListFilter(DIMENSION_ITEM_SOURCE, [SOURCE_PLUGILO]),
      buildInListFilter(DIMENSION_ITEM_CATEGORY, [CATEGORY_PLUGILO_VIDEO]),
    ];
    return {
      andGroup: {
        expressions: [...defaultFilters, ...(filters || [])],
      },
    };
  };

  const reportRequests = useMemo(() => {
    if (!dateStrings || !dateStrings[0] || !dateStrings[1]) return;

    return [
      // Content Active Views & Details
      {
        dateRanges: [
          {
            startDate: dateStrings[0],
            endDate: dateStrings[1],
          },
        ],
        dimensions: [
          {
            name: DIMENSION_DATE,
          },
          {
            name: DIMENSION_ITEM_CATEGORY,
          },
          {
            name: DIMENSION_EVENT_NAME,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: buildFilters([FILTER_EVENT_VIEW_ITEM_DETAILS]),
      },
      // Content Active Views from plugilo portal
      {
        dateRanges: [
          {
            startDate: dateStrings[0],
            endDate: dateStrings[1],
          },
        ],
        dimensions: [
          {
            name: DIMENSION_ITEM_ID,
          },
          {
            name: DIMENSION_ITEM_NAME,
          },
          {
            name: DIMENSION_ITEM_CATEGORY,
          },
          {
            name: DIMENSION_ITEM_CATEGORY2,
          },
          {
            name: DIMENSION_ITEM_URL,
          },
        ],
        metrics: [
          {
            name: METRIC_EVENT_COUNT,
          },
        ],
        dimensionFilter: buildFilters([FILTER_EVENT_VIEW_ITEM_DETAILS]),
        limit: 100,
        offset: 0,
      },
    ];
  }, [dateStrings]);

  useEffect(() => {
    if (!reportRequests) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        const data = await getBatchReports(reportRequests);
        setReports(get(data, "reports", []));
      } catch (error) {
        alertUnknownError();
        setReports([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [reportRequests]);

  const filterReport = useCallback((report) => {
    if (isEmpty(report)) return {};

    return report;
  }, []);

  const viewsReportData = useMemo(() => {
    return filterReport(reviseReport(reports[0]));
  }, [filterReport, reports]);

  const plugiloActiveViewsReportData = useMemo(() => {
    return filterReport(reviseReport(reports[1]));
  }, [filterReport, reports]);

  const activeViewsTrendTimeSeriesData = useMemo(() => {
    if (isEmpty(viewsReportData)) return [];

    const dataKeysMap = {};
    const rowsGroupedByDate = viewsReportData.rows
      .filter((row) =>
        [EVENT_VIEW_ITEM_DETAILS].includes(row[DIMENSION_EVENT_NAME])
      )
      .reduce((result, current) => {
        const date = current[DIMENSION_DATE];
        const category = current[DIMENSION_ITEM_CATEGORY];
        if (!result[date]) result[date] = {};

        result[date][DIMENSION_DATE] = date;
        result[date][category] = sum([
          result[date][category],
          current[METRIC_EVENT_COUNT],
        ]);
        result[date][METRIC_EVENT_COUNT] = sum([
          result[date][METRIC_EVENT_COUNT],
          current[METRIC_EVENT_COUNT],
        ]);
        dataKeysMap[category] = category;
        return result;
      }, {});

    return reviseTimeSeriesData(Object.values(rowsGroupedByDate), {
      dateKey: DIMENSION_DATE,
      startDate: dateStrings[0],
      endDate: dateStrings[1],
      dataKeys: [...Object.keys(dataKeysMap), METRIC_EVENT_COUNT],
    });
  }, [dateStrings, viewsReportData]);

  const activeViewsCount = useMemo(() => {
    if (isEmpty(activeViewsTrendTimeSeriesData)) return 0;

    return sum(
      activeViewsTrendTimeSeriesData.map((item) => item[METRIC_EVENT_COUNT])
    );
  }, [activeViewsTrendTimeSeriesData]);

  const videoActiveViewsCount = useMemo(() => {
    if (isEmpty(activeViewsTrendTimeSeriesData)) return 0;

    return sum(
      activeViewsTrendTimeSeriesData.map((row) => row[CATEGORY_PLUGILO_VIDEO])
    );
  }, [activeViewsTrendTimeSeriesData]);

  const plugiloContentActiveViewsByTypesPieChartData = useMemo(() => {
    if (isEmpty(viewsReportData)) return [];

    const rowsGroupedByCategory = viewsReportData.rows
      .filter((row) =>
        [EVENT_VIEW_ITEM_DETAILS].includes(row[DIMENSION_EVENT_NAME])
      )
      .reduce((result, current) => {
        const category = current[DIMENSION_ITEM_CATEGORY];
        if (!result[category]) result[category] = {};

        result[category][DIMENSION_ITEM_CATEGORY] =
          getContentTypeName(category);
        result[category][METRIC_EVENT_COUNT] = sum([
          result[category][METRIC_EVENT_COUNT],
          current[METRIC_EVENT_COUNT],
        ]);
        return result;
      }, {});

    return orderBy(Object.values(rowsGroupedByCategory), [DIMENSION_DATE]);
  }, [viewsReportData]);

  const plugiloContentActiveViewsTableData = useMemo(() => {
    if (isEmpty(plugiloActiveViewsReportData)) return [];

    return orderBy(
      plugiloActiveViewsReportData.rows,
      [METRIC_EVENT_COUNT],
      ["desc"]
    ).map((item) => ({ ...item, [DIMENSION_ITEM_ID_TEMP]: uuid() }));
  }, [plugiloActiveViewsReportData]);

  return (
    <Spin spinning={isLoading}>
      <ChartTitle>Views trend</ChartTitle>
      <Row gutter={GRID_GUTTER}>
        <Col xs={24} lg={12} className="mb-8">
          <Card size="small">
            <LineChart
              xAxisDataKey="date"
              data={activeViewsTrendTimeSeriesData}
              lines={[
                {
                  name: "Video",
                  dataKey: CATEGORY_PLUGILO_VIDEO,
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12} className="mb-8">
          {/* <Row gutter={GRID_GUTTER}>
            <Col span={24} className="mb-8">
              <Card size="small">
                <Scorecard
                  title="Active Views"
                  value={formatNumber(activeViewsCount)}
                  chartProps={{
                    data: activeViewsTrendTimeSeriesData,
                    lines: [
                      {
                        name: "Active Views",
                        dataKey: METRIC_EVENT_COUNT,
                      },
                    ],
                  }}
                />
              </Card>
            </Col>
          </Row> */}
          <Row gutter={GRID_GUTTER}>
            <Col span={8} className="mb-8">
              <Card size="small" className="lg:h-full">
                <Scorecard
                  title="Video"
                  value={formatNumber(videoActiveViewsCount)}
                  chartProps={{
                    data: activeViewsTrendTimeSeriesData,
                    lines: [
                      {
                        name: "Active Views",
                        dataKey: CATEGORY_PLUGILO_VIDEO,
                      },
                    ],
                  }}
                />
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={GRID_GUTTER}>
        {/* Most Viewed Contents from plugilo portal */}
        <Col xs={24} lg={12} className="mb-8">
          <ChartTitle>Top viewed contents</ChartTitle>
          <Card size="small" className="lg:h-full">
            <TableChart
              dataSource={plugiloContentActiveViewsTableData}
              rowKey={DIMENSION_ITEM_ID_TEMP}
              syncNames={syncNamesOptions}
              columns={[
                {
                  title: "Content",
                  dataIndex: DIMENSION_ITEM_URL,
                  key: DIMENSION_ITEM_URL,
                  render: (value, record) => (
                    <a href={value} target="_blank" rel="noreferrer">
                      {record[DIMENSION_ITEM_NAME]}
                    </a>
                  ),
                  ellipsis: true,
                },
                {
                  title: "Type",
                  dataIndex: DIMENSION_ITEM_CATEGORY,
                  key: DIMENSION_ITEM_CATEGORY,
                  render: (value) => <ContentType tag={value} />,
                  width: 80,
                },
                {
                  title: "Views",
                  dataIndex: METRIC_EVENT_COUNT,
                  key: METRIC_EVENT_COUNT,
                  width: 100,
                  align: "right",
                  render: (value) => formatNumber(value),
                },
              ]}
            />
          </Card>
        </Col>
        {/* <Col xs={24} lg={12} className="mb-8">
          <ChartTitle>Views by content type</ChartTitle>
          <Card size="small" className="lg:h-full">
            <div className="h-80">
              <PieChart
                data={plugiloContentActiveViewsByTypesPieChartData}
                dataKey={METRIC_EVENT_COUNT}
                nameKey={DIMENSION_ITEM_CATEGORY}
              />
            </div>
          </Card>
        </Col> */}
      </Row>
    </Spin>
  );
};

Marketing.propTypes = {};

export default Marketing;
